import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
// Controllers
import { LoadingController } from '@ionic/angular';
// Services
import { CoreService } from 'src/app/services/core.service';
import { Pilot, Vehicle } from '@mocca-technologies/model';
import { Battery } from '@mocca-technologies/model';
import { droneIcon } from 'src/assets/icons';
import { TimeFilter } from '../logbooks-time-filter/logbooks-time-filter.component';
import { LogbookService } from 'src/app/services/logbooks.service';

@Component({
  selector: 'app-logbooks-menu',
  templateUrl: './logbooks-menu.component.html',
  styleUrls: ['./logbooks-menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LogbooksMenuComponent  implements OnInit, OnChanges {

  /**
   * Output Variables
   * */

  @Output() sendSelectedValues = new EventEmitter<selectedValues>();
  @Output() expandedSectionsChange = new EventEmitter<Record<string, boolean>>();

  /**
   * Local variables
   * */

  droneIcon = droneIcon;

  /**
   * Input variables
   */

  @Input() pilotList: Pilot[];
  @Input() pilotsByOrganization: Map<string, Pilot[]> = new Map<string, Pilot[]>();
  @Input() vehicleList: Vehicle[];
  @Input() vehiclesByOrganization: Map<string, Vehicle[]> = new Map<string, Vehicle[]>();
  @Input() batteryList: Battery[];
  @Input() selectedValues: selectedValues = {
    selectedSection: null,
    selectedPilot: null,
    selectedVehicle: null,
    selectedBattery: null,
    timeFilter: {
      selectedFilter: 'months',
      selectedNumberOfMonths: 1,
      selectedNumberOfYears: 1,
      selectedYear: new Date().getFullYear()
    }
  }
  @Input() organizationNames: { [orgUuid: string]: string } = {};
  @Input() expandedSections: Record<string, boolean> = {};

  // #region --> Getters and Setters

  get filteredVehicleList() {
    return this.vehicleList?.filter(vehicle => vehicle?.plate && vehicle?.plate !== '');
  }

  get filteredPilotList() {
    return this.pilotList?.filter(pilot => pilot?.username && pilot?.username !== '');
  }

  get filteredBatteryList() {
    return this.batteryList?.filter(battery => battery?.sn && battery?.sn !== '');
  }

  // #endregion --> Getters and Setters

  constructor(
    public logbookService: LogbookService
  ) {}

  // #region --> Life Cycle Methods

  ngOnInit() {
    this.ensureExpandedStateInitialized();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedValues || changes.expandedSections) {
      this.ensureExpandedStateInitialized();
    }
  }

  // #endregion --> Life Cycle Methods

  // #region --> Select Methods

  /**
   * This method is used to select the document section. It selects de default values.
   * @param documentSection
   */

  selectDocumentSection(documentSection: string){
    this.toggleSectionExpand(documentSection);
  }

  toggleSectionExpand(documentSection: string) {
    this.setSectionExpanded(documentSection, !this.isExpanded(documentSection));
  }

  isExpanded(documentSection: string): boolean {
    return !!this.expandedSections?.[documentSection];
  }

  /**
   * This method is used to select the vehicle. When a vehicle is selected, the time filter is reseted.
   * @param vehicle
   */

  selectVehicle(vehicle: Vehicle){
    this.selectedValues.selectedSection = 'Drone';
    this.selectedValues.selectedVehicle = vehicle;
    this.selectedValues.selectedPilot = null;
    this.selectedValues.selectedBattery = null;
    this.setSectionExpanded('Drone', true, false);

    /*this.selectedValues.timeFilter = {
      selectedFilter: 'months',
      selectedNumberOfMonths: 1,
      selectedNumberOfYears: 1,
      selectedYear: new Date().getFullYear()
    }*/

    this.sendSelectedValues.emit(this.selectedValues);

  }

  /**
   * This method is used to select the pilot. When a pilot is selected, the time filter is reseted.
   * @param pilot
   */

  selectPilot(pilot: Pilot){
    this.selectedValues.selectedSection = 'Pilot';
    this.selectedValues.selectedPilot = pilot;
    this.selectedValues.selectedVehicle = null;
    this.selectedValues.selectedBattery = null;
    this.setSectionExpanded('Pilot', true, false);

    /*this.selectedValues.timeFilter = {
      selectedFilter: 'months',
      selectedNumberOfMonths: 1,
      selectedNumberOfYears: 1,
      selectedYear: new Date().getFullYear()
    }*/

    this.sendSelectedValues.emit(this.selectedValues);

  }

  /**
   * This method is used to select the battery. When a battery is selected, the time filter is reseted.
   * @param battery
   */

  selectBattery(battery: Battery){
    this.selectedValues.selectedSection = 'Batteries';
    this.selectedValues.selectedBattery = battery;
    this.selectedValues.selectedPilot = null;
    this.selectedValues.selectedVehicle = null;
    this.setSectionExpanded('Batteries', true, false);

    /*this.selectedValues.timeFilter = {
      selectedFilter: 'months',
      selectedNumberOfMonths: 1,
      selectedNumberOfYears: 1,
      selectedYear: new Date().getFullYear()
    }*/

    this.sendSelectedValues.emit(this.selectedValues);

  }

  // #endregion --> Select Methods

  private ensureExpandedStateInitialized() {
    if (!this.expandedSections) {
      this.expandedSections = {};
    }

    const currentSection = this.selectedValues?.selectedSection;
    if (currentSection && this.expandedSections[currentSection] === undefined) {
      this.setSectionExpanded(currentSection, true, false);
    }
  }

  private setSectionExpanded(documentSection: string, expanded: boolean, emitChange = true) {
    this.expandedSections = {
      ...this.expandedSections,
      [documentSection]: expanded
    };

    if (emitChange) {
      this.expandedSectionsChange.emit(this.expandedSections);
    }
  }

}

export interface selectedValues {
  selectedSection: string;
  selectedPilot: Pilot;
  selectedVehicle: Vehicle;
  selectedBattery: Battery;
  timeFilter: TimeFilter;
}
